/**
 * Google Analytics 4 Hook for Strider
 * Tracks page views, events, and user interactions
 */

// GA4 Measurement ID
const GA_MEASUREMENT_ID = 'G-R1952760F0';

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Initialize Google Analytics
 * Called once on app mount
 */
export const initAnalytics = () => {
  if (!isGtagAvailable()) {
    console.log('Google Analytics not loaded yet');
    return;
  }
  
  // Track initial page view
  trackPageView(window.location.pathname, document.title);
  
  console.log('Google Analytics initialized: ' + GA_MEASUREMENT_ID);
};

/**
 * Track a page view
 * @param {string} path - The page path
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title,
  });
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('event', eventName, params);
};

/**
 * Track a user action (alias for trackEvent for backward compatibility)
 */
export const trackAction = (action, category = 'engagement', label = '', value = 0) => {
  trackEvent(action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};

/**
 * Increment page count (for internal analytics)
 */
export const incrementPageCount = () => {
  // This can be used to track unique page loads
  trackEvent('page_load', {
    page_path: window.location.pathname,
  });
};

// Pre-defined event trackers for common actions

/**
 * Track venue view
 */
export const trackVenueView = (venueId, venueName, venueType, city) => {
  trackEvent('view_item', {
    item_id: venueId,
    item_name: venueName,
    item_category: venueType,
    item_variant: city,
  });
};

/**
 * Track venue search
 */
export const trackSearch = (searchTerm, resultsCount) => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
  });
};

/**
 * Track filter usage
 */
export const trackFilter = (filterType, filterValue) => {
  trackEvent('select_content', {
    content_type: 'filter',
    filter_type: filterType,
    filter_value: filterValue,
  });
};

/**
 * Track review submission
 */
export const trackReviewSubmit = (venueId, rating) => {
  trackEvent('review_submit', {
    venue_id: venueId,
    rating: rating,
  });
};

/**
 * Track location submission
 */
export const trackLocationSubmit = (venueType, city) => {
  trackEvent('location_submit', {
    venue_type: venueType,
    city: city,
  });
};

/**
 * Track WiFi speed test
 */
export const trackSpeedTest = (venueId, downloadSpeed, uploadSpeed) => {
  trackEvent('wifi_speed_test', {
    venue_id: venueId,
    download_mbps: downloadSpeed,
    upload_mbps: uploadSpeed,
  });
};

/**
 * Track venue favorite
 */
export const trackFavorite = (venueId, venueName, action) => {
  trackEvent(action === 'add' ? 'add_to_wishlist' : 'remove_from_wishlist', {
    item_id: venueId,
    item_name: venueName,
  });
};

/**
 * Track directions click
 */
export const trackDirections = (venueId, venueName) => {
  trackEvent('get_directions', {
    venue_id: venueId,
    venue_name: venueName,
  });
};

/**
 * Track share action
 */
export const trackShare = (venueId, venueName, method) => {
  trackEvent('share', {
    item_id: venueId,
    item_name: venueName,
    method: method,
  });
};

/**
 * Track outbound link clicks
 */
export const trackOutboundLink = (url, linkType) => {
  trackEvent('click', {
    event_category: 'outbound',
    event_label: url,
    link_type: linkType,
  });
};

/**
 * Track user sign up
 */
export const trackSignUp = (method) => {
  trackEvent('sign_up', {
    method: method,
  });
};

/**
 * Track user login
 */
export const trackLogin = (method) => {
  trackEvent('login', {
    method: method,
  });
};

/**
 * Track premium conversion
 */
export const trackPremiumConversion = (planType, value) => {
  trackEvent('purchase', {
    currency: 'GBP',
    value: value,
    items: [{
      item_name: `Premium ${planType}`,
      item_category: 'subscription',
      price: value,
      quantity: 1,
    }],
  });
};

export default {
  trackPageView,
  trackEvent,
  trackVenueView,
  trackSearch,
  trackFilter,
  trackReviewSubmit,
  trackLocationSubmit,
  trackSpeedTest,
  trackFavorite,
  trackDirections,
  trackShare,
  trackOutboundLink,
  trackSignUp,
  trackLogin,
  trackPremiumConversion,
};
